import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wrench, Loader2, Plus, Search, Pencil, X } from "lucide-react";
import { fetchServiceRecords, createServiceRecord, updateServiceRecord, fetchAdminVehicles } from "@/lib/api";
import { formatDMY } from "@/lib/date";
import { DateField } from "@/components/admin/DateField";

const TYPES = ["Periodic Service", "Repair", "Tyre", "Battery", "Other"];
const hideNextDate = (t) => t === "Tyre";
const hideMileage = (t) => t === "Tyre" || t === "Battery";
const STATUSES = ["Scheduled", "Completed", "Cancelled"];
const PROTON = "Proton e.MAS 5";

const empty = {
  vehicle: "", maintenance_type: "Periodic Service", status: "Scheduled",
  service_date: "", cost: "", next_maintenance_date: "", next_service_mileage: "",
  next_service_unit: "km", road_tax_expiry: "", notes: "", completion_date: "", cancellation_reason: "",
};

const statusStyle = {
  Scheduled: "bg-slate-100 text-slate-700 ring-slate-300",
  Overdue: "bg-red-50 text-red-700 ring-red-300",
  Completed: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  Cancelled: "bg-rose-50 text-rose-600 ring-rose-300",
};

export const ServiceDetailsTab = ({ onChanged }) => {
  const [vehicles, setVehicles] = useState([]);
  const [items, setItems] = useState([]);
  const [protonExpiry, setProtonExpiry] = useState(null);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fVehicle, setFVehicle] = useState("all");
  const [fStatus, setFStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchServiceRecords({
        ...(fVehicle !== "all" ? { vehicle: fVehicle } : {}),
        ...(fStatus !== "all" ? { status: fStatus } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      });
      setItems(data.items);
      setProtonExpiry(data.proton_road_tax_expiry);
    } catch {
      toast.error("Failed to load service records");
    } finally {
      setLoading(false);
    }
  }, [fVehicle, fStatus, search]);

  useEffect(() => { fetchAdminVehicles().then((v) => setVehicles(v)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(empty); setEditId(null); };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      vehicle: r.vehicle, maintenance_type: r.maintenance_type, status: r.status,
      service_date: r.service_date || "", cost: r.cost != null ? String(r.cost) : "",
      next_maintenance_date: r.next_maintenance_date || "", next_service_mileage: r.next_service_mileage != null ? String(r.next_service_mileage) : "",
      next_service_unit: r.next_service_unit || "km",
      road_tax_expiry: r.vehicle === PROTON ? (protonExpiry || "") : "",
      notes: r.notes || "", completion_date: r.completion_date || "", cancellation_reason: r.cancellation_reason || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!form.vehicle) { toast.error("Select a vehicle"); return; }
    const payload = {
      vehicle: form.vehicle, maintenance_type: form.maintenance_type, status: form.status,
      service_date: form.service_date || null,
      cost: form.cost !== "" ? Number(form.cost) : null,
      next_maintenance_date: !hideNextDate(form.maintenance_type) && form.next_maintenance_date ? form.next_maintenance_date : null,
      next_service_mileage: !hideMileage(form.maintenance_type) && form.next_service_mileage !== "" ? parseInt(form.next_service_mileage, 10) : null,
      next_service_unit: !hideMileage(form.maintenance_type) && form.next_service_mileage !== "" ? "km" : null,
      road_tax_expiry: form.vehicle === PROTON && form.road_tax_expiry ? form.road_tax_expiry : null,
      notes: form.notes || null,
      completion_date: form.completion_date || null,
      cancellation_reason: form.cancellation_reason || null,
    };
    setSaving(true);
    try {
      if (editId) {
        const res = await updateServiceRecord(editId, payload);
        toast.success(res.created_next_schedule ? "Updated — next schedule created" : "Service record updated");
      } else {
        await createServiceRecord(payload);
        toast.success("Service record added");
      }
      resetForm();
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save record");
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]";
  const lbl = "mb-1 block text-xs font-semibold text-[#0F172A]";

  return (
    <div className="space-y-6" data-testid="service-details-tab">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Wrench className="h-5 w-5 text-[#5C2D91]" />
          <h2 className="font-heading text-lg font-semibold text-[#0F172A]">{editId ? "Edit Service Record" : "Add Service Record"}</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div><label className={lbl}>Vehicle *</label>
            <select className={inp} value={form.vehicle} onChange={(e) => set("vehicle", e.target.value)} data-testid="svc-vehicle">
              <option value="">Select…</option>
              {vehicles.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Maintenance Type</label>
            <select className={inp} value={form.maintenance_type} onChange={(e) => set("maintenance_type", e.target.value)} data-testid="svc-type">
              {(TYPES.includes(form.maintenance_type) ? TYPES : [...TYPES, form.maintenance_type]).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Status</label>
            <select className={inp} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="svc-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Service Date{form.status === "Completed" ? " *" : ""}</label>
            <DateField value={form.service_date} onChange={(v) => set("service_date", v)} testid="svc-service-date" />
          </div>
          <div><label className={lbl}>Maintenance Cost (RM)</label>
            <input type="number" step="0.01" min="0" className={inp} value={form.cost} onChange={(e) => set("cost", e.target.value)} placeholder="0.00" data-testid="svc-cost" />
          </div>
          {!hideNextDate(form.maintenance_type) && (
          <div><label className={lbl}>Next Maintenance Date{["Scheduled", "Completed"].includes(form.status) ? " *" : ""}</label>
            <DateField value={form.next_maintenance_date} onChange={(v) => set("next_maintenance_date", v)} testid="svc-next-date" />
          </div>
          )}
          {!hideMileage(form.maintenance_type) && (
          <div><label className={lbl}>Next Service Mileage (km)</label>
            <div className="flex gap-2">
              <input type="number" min="0" className={inp} value={form.next_service_mileage} onChange={(e) => set("next_service_mileage", e.target.value)} placeholder="e.g. 10000" data-testid="svc-mileage" />
              <span className="flex items-center rounded-lg border border-slate-300 bg-slate-50 px-3 text-sm text-[#475569]" data-testid="svc-unit">km</span>
            </div>
          </div>
          )}
          {form.vehicle === PROTON && (
            <div><label className={lbl}>Road Tax Expiry (Proton only)</label>
              <DateField value={form.road_tax_expiry} onChange={(v) => set("road_tax_expiry", v)} testid="svc-road-tax" />
            </div>
          )}
          {form.status === "Completed" && (
            <div><label className={lbl}>Completion Date *</label>
              <DateField value={form.completion_date} onChange={(v) => set("completion_date", v)} testid="svc-completion-date" />
            </div>
          )}
          {form.status === "Cancelled" && (
            <div className="sm:col-span-2"><label className={lbl}>Cancellation Reason *</label>
              <input type="text" className={inp} value={form.cancellation_reason} onChange={(e) => set("cancellation_reason", e.target.value)} data-testid="svc-cancel-reason" />
            </div>
          )}
          <div className="sm:col-span-2 lg:col-span-3"><label className={lbl}>Notes</label>
            <textarea rows={2} className={inp} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="svc-notes" />
          </div>
        </div>
        <div className="mt-4 flex gap-3">
          <button onClick={submit} disabled={saving} className="flex items-center gap-2 rounded-lg bg-[#5C2D91] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] disabled:opacity-60" data-testid="svc-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {editId ? "Update" : "Save"}
          </button>
          {editId && (
            <button onClick={resetForm} className="flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-[#0F172A] hover:bg-slate-50" data-testid="svc-cancel-edit">
              <X className="h-4 w-4" /> Cancel
            </button>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-heading text-lg font-semibold text-[#0F172A]">Maintenance Records</h3>
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-nowrap sm:items-center">
            <div className="relative col-span-2 w-full sm:w-56">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94a3b8]" />
              <input className={`${inp} pl-8`} placeholder="Search type/notes" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="svc-search" />
            </div>
            <select className={`${inp} sm:w-48`} value={fVehicle} onChange={(e) => setFVehicle(e.target.value)} data-testid="svc-filter-vehicle">
              <option value="all">All vehicles</option>
              {vehicles.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
            </select>
            <select className={`${inp} sm:w-40`} value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="svc-filter-status">
              <option value="all">All statuses</option>
              {["Scheduled", "Overdue", "Completed", "Cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        {protonExpiry != null && (
          <p className="mb-3 text-xs font-medium text-[#5C2D91]" data-testid="svc-proton-roadtax">Proton e.MAS 5 road tax expiry: <span className="font-bold">{formatDMY(protonExpiry)}</span></p>
        )}
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-[#64748B]"><Loader2 className="h-4 w-4 animate-spin text-[#5C2D91]" /> Loading…</div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#64748B]" data-testid="svc-empty">No service records found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="mobile-cards w-full min-w-[820px] text-left text-sm" data-testid="svc-table">
              <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-[#64748B]">
                <th className="py-3 pr-4">Vehicle</th><th className="py-3 pr-4">Type</th><th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4">Service Date</th><th className="py-3 pr-4">Next Date</th><th className="py-3 pr-4">Cost (RM)</th><th className="py-3 pr-4">Action</th>
              </tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`svc-row-${r.id}`}>
                    <td data-label="Vehicle" className="py-3 pr-4 font-medium text-[#0F172A]">{r.vehicle}</td>
                    <td data-label="Type" className="py-3 pr-4 text-[#0F172A]">{r.maintenance_type}</td>
                    <td data-label="Status" className="py-3 pr-4"><span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusStyle[r.effective_status]}`}>{r.effective_status}</span></td>
                    <td data-label="Service Date" className="py-3 pr-4 text-[#0F172A]">{r.service_date ? formatDMY(r.service_date) : "—"}</td>
                    <td data-label="Next Date" className="py-3 pr-4 text-[#0F172A]">{r.next_maintenance_date ? formatDMY(r.next_maintenance_date) : "—"}</td>
                    <td data-label="Cost (RM)" className="py-3 pr-4 text-[#0F172A]">{r.cost != null ? r.cost.toFixed(2) : "—"}</td>
                    <td className="py-3 pr-4">
                      <button onClick={() => startEdit(r)} className="flex items-center gap-1.5 rounded-lg bg-[#5C2D91] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#4A2475]" data-testid={`svc-edit-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" /> View / Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
